import type { ElectronApi } from '../shared/electron-api';
import { type IceServer } from '../shared/ice';
import { type FileAck, type FileMeta, type FileResumeResponse } from '../shared/filexfer';
import { type WebRtcConnectionMetrics, type WebRtcTrafficSample } from '../shared/rtc-path';
import { type RemoteStreamSettings } from '../shared/stream-profile';
import { type ClipboardFileData } from '../shared/clipboard-file';
import { type InputAckMessage, type InputAckableType, type InputMessage, type MouseButton } from '../shared/datachannel';
import { DataChannelMessageGuard } from '../shared/datachannel-guard';
import { normalizeSessionAccessMode, type SessionAccessMode } from '../shared/session-mode';
import {
  appendConnectionHistory,
  createInitialConnectionState,
  type ConnectionDiagnostics,
  type ConnectionPhase,
  type ConnectionReason,
  type ConnectionStateEvent,
  type ConnectionState,
} from '../shared/connection-state';
import {
  type RemotePeerInfo,
  type ControllerCallbacks,
  type ReceivingFile,
  type ReceivingClipboardBatch,
  type PendingClipboardTransfer,
  type PendingFileClipboardTransfer,
  type PendingFileAck,
  type PendingFileResume,
} from './controller/types';
import { controllerSignaling } from './controller/signaling';
import { controllerPeer } from './controller/peer';
import { controllerControl } from './controller/control';
import { controllerClipboard } from './controller/clipboard';
import { controllerFile } from './controller/file';

export { type RemotePeerInfo, type ControllerCallbacks } from './controller/types';

export class ControllerSession {
  api: ElectronApi;
  cb: ControllerCallbacks;
  ws: WebSocket | null = null;
  pc: RTCPeerConnection | null = null;
  dc: RTCDataChannel | null = null;
  pointerDc: RTCDataChannel | null = null;
  wheelDc: RTCDataChannel | null = null;
  cursorDc: RTCDataChannel | null = null;
  clipboardDc: RTCDataChannel | null = null;
  disposed = true;
  iceServers: IceServer[] | null = null;
  currentWsUrl = '';
  currentPairMsg: unknown = null;
  // 设备码配对密码只在当前会话内保留；配对成功后的可重用消息不会继续携带它。
  guestPassword: string | null = null;
  reconnectTimer: NodeJS.Timeout | null = null;
  reconnectAttempt = 0;
  openGeneration = 0;
  sessionId: string | null = null;
  heartbeatTimer: NodeJS.Timeout | null = null;
  signalingHeartbeatAckAt = 0;
  controlHeartbeatTimer: NodeJS.Timeout | null = null;
  controlHeartbeatSentAt = 0;
  controlRttMs: number | null = null;
  // 关键鼠标/键盘操作的本地发送时间；回执只用于测量，不参与下一条输入调度。
  pendingInputAcks = new Map<string, { sentAt: number; inputType: InputAckableType }>();
  inputRttMs: number | null = null;
  // 记录控制端当前仍按住的输入。重建 RTC 通道后会把这份“当前状态”
  // 发给被控端，避免断线恰好发生在 keyup/mouseup 边界时两端状态不一致。
  heldInputKeys = new Set<string>();
  heldInputButtons = new Set<MouseButton>();
  iceRestartTimer: NodeJS.Timeout | null = null;
  iceRestartWatchdogTimer: NodeJS.Timeout | null = null;
  iceRestartInFlight = false;
  pendingIceCandidates: RTCIceCandidateInit[] = [];
  pathTimer: NodeJS.Timeout | null = null;
  connectionActivityTimer: NodeJS.Timeout | null = null;
  stalledReconnectTimer: NodeJS.Timeout | null = null;
  videoTrackRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
  pairWaitTimer: NodeJS.Timeout | null = null;
  lastInboundActivityAt = 0;
  lastControlActivityAt = 0;
  lastVideoActivityAt = 0;
  controlHeartbeatAckAt = 0;
  lastInboundBytes: number | null = null;
  lastInboundFrames: number | null = null;
  connectionStalled = false;
  lastPathSignature = '';
  lastTrafficSample: WebRtcTrafficSample | null = null;
  refreshingConnection = false;
  fileDc: RTCDataChannel | null = null;
  fileRecv: ReceivingFile | null = null;
  fileReceiveTimer: ReturnType<typeof setTimeout> | null = null;
  fileReceiveResumeUntil = 0;
  fileClipboardBatch: ReceivingClipboardBatch | null = null;
  pendingFileClipboardTransfers = new Map<string, PendingFileClipboardTransfer>();
  pendingFileAcks = new Map<string, PendingFileAck>();
  // 接收端按块回执的最近连续确认点；跨 RTC 通道重建保留，任务结束后清理。
  fileAckOffsets = new Map<string, number>();
  // 当前发送任务收到的对端回执；与恢复检查点分开，便于兼容旧客户端。
  fileProgressAckOffsets = new Map<string, number>();
  pendingFileResumes = new Map<string, PendingFileResume>();
  fileSendTail: Promise<unknown> = Promise.resolve();
  fileMessageTail: Promise<void> = Promise.resolve();
  activeFileTransfers = new Set<string>();
  cancelledFileTransfers = new Set<string>();
  remotePeer: RemotePeerInfo | null = null;
  remoteVideoTrack: MediaStreamTrack | null = null;
  streamReady = false;
  videoEnabled = true;
  controlReady = false;
  remoteInputReady: boolean | null = null;
  accessMode: SessionAccessMode = 'control';
  lastControlWarningAt = 0;
  clipboardSequence = 0;
  // 系统剪贴板写入可能被 Windows/其他进程短暂占用。串行化写入并用
  // revision 丢弃过期内容，避免旧的异步写入晚完成后覆盖最新复制结果。
  clipboardReceiveRevision = 0;
  clipboardWriteTail: Promise<void> = Promise.resolve();
  pendingClipboardTransfers = new Map<string, PendingClipboardTransfer>();
  // 每个会话的入站消息都经过通道级大小/类型/频率边界；短暂重连不会
  // 复用旧 RTC 通道，但保留 guard 实例只影响当前 1 秒窗口。
  controlMessageGuard = new DataChannelMessageGuard('control');
  clipboardMessageGuard = new DataChannelMessageGuard('clipboard');
  cursorMessageGuard = new DataChannelMessageGuard('cursor');
  fileMessageGuard = new DataChannelMessageGuard('file');
  connectionState: ConnectionState = createInitialConnectionState();
  reconnectCount = 0;
  connectionStartedAt: number | null = null;
  connectedAt: number | null = null;
  firstFrameMs: number | null = null;
  lastConnectionMetrics: WebRtcConnectionMetrics | null = null;
  connectionHistory: ConnectionStateEvent[] = [];

  constructor(api: ElectronApi, cb: ControllerCallbacks) {
    this.api = api;
    this.cb = cb;
  }

  setAccessMode(mode: SessionAccessMode): void {
    this.accessMode = normalizeSessionAccessMode(mode);
  }

  resetConnectionTracking(): void {
    this.connectionState = createInitialConnectionState();
    this.connectionStartedAt = null;
    this.connectedAt = null;
    this.firstFrameMs = null;
    this.lastConnectionMetrics = null;
    this.connectionHistory = [];
    this.reconnectCount = 0;
    this.lastInboundActivityAt = 0;
    this.lastControlActivityAt = 0;
    this.lastVideoActivityAt = 0;
    this.controlHeartbeatAckAt = 0;
    this.controlHeartbeatSentAt = 0;
    this.controlRttMs = null;
    this.pendingInputAcks.clear();
    this.inputRttMs = null;
    this.heldInputKeys.clear();
    this.heldInputButtons.clear();
    this.cb.onInputRtt?.(null);
    this.clipboardReceiveRevision += 1;
  }

  /**
   * 发布统一的会话状态。底层 WebSocket/WebRTC 的细节仍留在各自模块，
   * 但 UI 和诊断只消费这一份状态，避免多个回调互相覆盖造成假状态。
   */
  setConnectionState(
    phase: ConnectionPhase,
    reason: ConnectionReason,
    message: string,
    retryable: boolean,
  ): void {
    const now = Date.now();
    if (phase === 'connecting' && this.connectionState.phase === 'closed') {
      this.connectionStartedAt = now;
      this.connectedAt = null;
    }
    if (phase === 'connecting' && this.connectionStartedAt === null) this.connectionStartedAt = now;
    if (phase === 'connected' && this.connectedAt === null) this.connectedAt = now;
    const next: ConnectionState = {
      phase,
      reason,
      message,
      retryable,
      reconnectAttempt: this.reconnectAttempt,
      lastActivityAt: this.lastInboundActivityAt || null,
      startedAt: this.connectionStartedAt,
      connectedAt: this.connectedAt,
      updatedAt: now,
    };
    const previous = this.connectionState;
    this.connectionState = next;
    if (
      previous.phase === next.phase &&
      previous.reason === next.reason &&
      previous.message === next.message &&
      previous.retryable === next.retryable &&
      previous.reconnectAttempt === next.reconnectAttempt
    ) return;
    this.connectionHistory = appendConnectionHistory(this.connectionHistory, next, now);
    this.cb.onConnectionState?.(next);
  }

  getConnectionDiagnostics(): ConnectionDiagnostics {
    const now = Date.now();
    const lastActivityAt = this.lastInboundActivityAt || null;
    const lastControlActivityAt = this.lastControlActivityAt || null;
    const lastVideoActivityAt = this.lastVideoActivityAt || null;
    const readyState = this.ws?.readyState;
    const signalingState = readyState === WebSocket.CONNECTING
      ? 'connecting'
      : readyState === WebSocket.OPEN
        ? 'open'
        : readyState === WebSocket.CLOSING
          ? 'closing'
          : readyState === WebSocket.CLOSED
            ? 'closed'
            : 'idle';
    return {
      ...this.connectionState,
      reconnectAttempt: this.reconnectAttempt,
      lastActivityAt,
      startedAt: this.connectionStartedAt,
      connectedAt: this.connectedAt,
      reconnectCount: this.reconnectCount,
      signalingState,
      peerConnectionState: this.pc?.connectionState || 'none',
      iceConnectionState: this.pc?.iceConnectionState || 'none',
      controlReady: this.controlReady,
      streamReady: this.streamReady,
      remoteInputReady: this.remoteInputReady,
      peerDeviceId: this.remotePeer?.deviceId || null,
      lastActivityAgeMs: lastActivityAt === null ? null : Math.max(0, now - lastActivityAt),
      lastControlActivityAgeMs: lastControlActivityAt === null ? null : Math.max(0, now - lastControlActivityAt),
      lastVideoActivityAgeMs: lastVideoActivityAt === null ? null : Math.max(0, now - lastVideoActivityAt),
      lastControlActivityAt,
      lastVideoActivityAt,
      firstFrameMs: this.firstFrameMs,
      videoWidth: this.lastConnectionMetrics?.videoWidth ?? null,
      videoHeight: this.lastConnectionMetrics?.videoHeight ?? null,
      videoCodec: this.lastConnectionMetrics?.videoCodec ?? null,
      videoImplementation: this.lastConnectionMetrics?.videoImplementation ?? null,
      inputRttMs: this.inputRttMs,
      stateHistory: this.connectionHistory.map((event) => ({ ...event })),
    };
  }
}

// ControllerSession 的方法体拆分到 ./controller/* 子模块，运行时通过 Object.assign 挂到原型。
// 下面的接口合并仅用于补全类型，不影响运行时行为。
export interface ControllerSession {
  connectCentral(token: string, targetDeviceId: string): Promise<void>;
  connectGuest(code: string, password: string, controllerKey: string, controllerDeviceId?: string, accessMode?: SessionAccessMode): void;
  connectCentral(token: string, targetDeviceId: string, accessMode?: SessionAccessMode): Promise<void>;
  connectLan(addr: string, lanPasscode: string, accessMode?: SessionAccessMode): Promise<void>;
  setAccessMode(mode: SessionAccessMode): void;
  resetConnectionTracking(): void;
  setConnectionState(phase: ConnectionPhase, reason: ConnectionReason, message: string, retryable: boolean): void;
  getConnectionDiagnostics(): ConnectionDiagnostics;
  controllerIdentity(): {
    controllerPlatform: string;
    controllerName: string;
    controllerInput: 'pointer' | 'touch';
  };
  getControllerDeviceId(): Promise<string | undefined>;
  open(wsUrl: string, pairMsg: unknown, fetchIce?: boolean, preserveFileTransfer?: boolean): void;
  scheduleReconnect(): void;
  toWsUrl(addr: string): string;
  startHeartbeat(ws: WebSocket, generation: number): void;
  stopHeartbeat(): void;
  releasePair(ws?: WebSocket | null, reason?: string): void;
  startControlHeartbeat(): void;
  stopControlHeartbeat(): void;
  recordControlHeartbeatAck(echoedAt?: number): void;
  recordInputAck(message: InputAckMessage): void;
  sendSignal(data: unknown): void;
  onSignal(msg: {
    type: string;
    deviceId?: string;
    platform?: string;
    hostname?: string;
    deviceName?: string;
    accessMode?: SessionAccessMode;
    reason?: string;
    data?: unknown;
    message?: string;
    ts?: number;
  }): void;
  dispose(): void;
  onWebRtcSignal(data: { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit }): Promise<void>;
  refreshConnection(): Promise<void>;
  startConnectionActivityWatch(): void;
  stopConnectionActivityWatch(): void;
  markConnectionActivity(): void;
  markControlActivity(): void;
  markVideoActivity(): void;
  bindRemoteVideoTrack(track: MediaStreamTrack): void;
  scheduleStalledReconnect(): void;
  reconnectFromStall(): void;
  recover(): void;
  closePeerConnection(preserveFileTransfer?: boolean): void;
  startPairWaitTimer(): void;
  stopPairWaitTimer(): void;
  cancelIceRestart(): void;
  scheduleIceRestart(delay: number): void;
  restart(): Promise<void>;
  onControl(raw: unknown, sourceChannel?: RTCDataChannel): void;
  onCursorMessage(raw: unknown, sourceChannel?: RTCDataChannel): void;
  sendInput(msg: InputMessage): void;
  sendInputState(): void;
  sendVideoSettings(settings: RemoteStreamSettings): boolean;
  setVideoEnabled(enabled: boolean): void;
  onClipboardMessage(raw: unknown, replyChannel: RTCDataChannel | null): void;
  sendClipboardAck(transferId: string, ok: boolean, preferredChannel: RTCDataChannel | null): void;
  requestRemoteClipboard(): void;
  sendClipboardText(text: string, html?: string, rtf?: string): Promise<boolean>;
  pasteToRemote(): Promise<boolean>;
  sendClipboardFiles(files: ClipboardFileData[]): Promise<boolean>;
  sendClipboardFilesNow(files: ClipboardFileData[]): Promise<boolean>;
  clipboardChannel(): RTCDataChannel | null;
  nextClipboardId(kind: 'read' | 'write'): string;
  sendClipboardAttempt(pending: PendingClipboardTransfer): void;
  finishClipboardTransfer(id: string, ok: boolean): void;
  clearClipboardTransfers(): void;
  setupFileChannel(): void;
  onFileMessage(data: unknown, sourceChannel?: RTCDataChannel): Promise<void>;
  armFileReceiveTimeout(): void;
  clearFileReceiveTimeout(): void;
  clearFileResumeWaiters(): void;
  clearFileAckWaiters(): void;
  clearFileAckWaiter(id: string): void;
  waitForFileResume(id: string): Promise<FileResumeResponse | null>;
  waitForFileChannel(transferId?: string): Promise<RTCDataChannel>;
  sendFileResumeResponse(response: FileResumeResponse): void;
  handleFileResumeResponse(response: FileResumeResponse): void;
  waitForFileCompletionAck(id: string): Promise<boolean | null>;
  sendFileAck(message: FileAck): boolean;
  handleFileAck(message: FileAck): void;
  sendFileProgressAck(file: ReceivingFile, force?: boolean): void;
  requestFileResume(
    file: File | ClipboardFileData,
    extra: Pick<FileMeta, 'purpose' | 'clipboardId' | 'clipboardIndex' | 'clipboardTotal'>,
    id: string,
    channel: RTCDataChannel,
  ): Promise<FileResumeResponse | null>;
  flushReceivedFileStream(file: ReceivingFile): Promise<void>;
  cancelReceivedFileStream(file: ReceivingFile): Promise<void>;
  sendFile(file: File, transferId?: string): Promise<void>;
  cancelFileTransfer(id: string, reason?: string): void;
  sendFileCancel(id: string, reason?: string): void;
  handleFileCancel(message: { type: 'file-cancel'; id: string; reason?: string }): void;
  enqueueFileSend<T>(job: () => Promise<T>): Promise<T>;
  sendFilePayload(
    file: File | ClipboardFileData,
    extra: Pick<FileMeta, 'purpose' | 'clipboardId' | 'clipboardIndex' | 'clipboardTotal'>,
    transferId?: string,
    onProgress?: (transferred: number) => void,
    allowResume?: boolean,
  ): Promise<void>;
  combineReceivedFile(file: ReceivingFile): ArrayBuffer | null;
  sendFileClipboardAck(id: string, ok: boolean, reason?: string): void;
  finishFileClipboardTransfer(id: string, ok: boolean): void;
  clearFileClipboardTransfers(): void;
  waitForFileDrain(channel: RTCDataChannel, transferId?: string): Promise<void>;
}

Object.assign(
  ControllerSession.prototype,
  controllerSignaling,
  controllerPeer,
  controllerControl,
  controllerClipboard,
  controllerFile,
);
